import { useState } from "react";
import { Link2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const isValidImageUrl = (u) => /^https?:\/\/\S+$/i.test((u || "").trim());

export function ImageUrlInput({ onSet, testid = "image-url", placeholder = "Paste image URL — ImageKit CDN or any direct image link", autoFocus = false }) {
  const [url, setUrl] = useState("");
  const apply = () => {
    const u = url.trim();
    if (!isValidImageUrl(u)) return toast.error("Enter a valid image URL starting with http:// or https://");
    onSet(u);
  };
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <Input value={url} autoFocus={autoFocus} onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
        placeholder={placeholder} className="rounded-lg h-9 text-xs flex-1" data-testid={`${testid}-input`} />
      <button type="button" onClick={apply} disabled={!isValidImageUrl(url)}
        className="rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-[11px] font-bold px-3 h-9 shrink-0 transition-colors"
        data-testid={`${testid}-apply`}>Use image</button>
    </div>
  );
}

export function ImageLinkDialog({ open, onClose, onSet, testid = "image-link-dialog", title = "Use image from link", successMessage = "Image linked from URL" }) {
  const [url, setUrl] = useState("");
  const [previewErr, setPreviewErr] = useState(false);
  if (!open) return null;
  const valid = isValidImageUrl(url);
  const apply = () => {
    onSet(url.trim());
    setUrl("");
    setPreviewErr(false);
    onClose();
    toast.success(successMessage);
  };
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose} data-testid={testid}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-slate-900">{title}</p>
            <p className="text-xs text-slate-400 mt-0.5">Paste a direct image link (e.g. from your ImageKit CDN). The image stays on your CDN — nothing is re-uploaded to our storage, keeping the site lightweight.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-300 hover:text-slate-600 shrink-0" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
        </div>
        <Input value={url} autoFocus onChange={(e) => { setUrl(e.target.value); setPreviewErr(false); }}
          onKeyDown={(e) => { if (e.key === "Enter" && valid) { e.preventDefault(); apply(); } }}
          placeholder="https://ik.imagekit.io/your-id/image.jpg" className="mt-3 rounded-xl" data-testid={`${testid}-input`} />
        {valid && !previewErr && <img src={url.trim()} alt="Link preview" onError={() => setPreviewErr(true)} className="mt-3 h-24 max-w-full w-auto rounded-lg border border-slate-200 object-contain bg-slate-50" data-testid={`${testid}-preview`} />}
        {valid && previewErr && <p className="mt-3 text-[11px] font-semibold text-amber-600" data-testid={`${testid}-preview-error`}>Preview unavailable — make sure this is a direct, public image link. You can still use it.</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 px-4 h-9 text-xs font-bold text-slate-500 hover:bg-slate-50" data-testid={`${testid}-cancel`}>Cancel</button>
          <button type="button" onClick={apply} disabled={!valid} className="rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-40 px-4 h-9 text-xs font-bold text-white transition-colors" data-testid={`${testid}-apply`}>Use this image</button>
        </div>
      </div>
    </div>
  );
}

export function ViaLinkButton({ onSet, testid, label = "via link", title, successMessage, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Paste image URL (ImageKit / CDN link)"
        className={`text-[10px] font-bold flex items-center gap-0.5 text-slate-400 hover:text-violet-600 transition-colors ${className}`}
        data-testid={testid}>
        <Link2 className="w-3 h-3" /> {label}
      </button>
      <ImageLinkDialog open={open} onClose={() => setOpen(false)} onSet={onSet} testid={`${testid}-dialog`} title={title} successMessage={successMessage} />
    </>
  );
}
